import { useState, useCallback, useEffect, useRef } from "react";
import { useGlobalAPI } from "../../../../hooks/useGlobalAPI";
import { getOCELMetadata, getResourceLastActiveDate } from "../../../../services/api/ocel.api";
import { useOcelMetadata } from "../../../../stores/OcelContext";
import { useGlobalFilters } from "../../../../stores/GlobalFilterContext/GlobalFilterContext";
import { computeDisplayRange, formatComparisonPeriodLabel } from "../utils/periodRange";
import { getComparisonRange, clampRangeEndToToday } from "../../_shared/comparisonPeriod";
import { getBerlinToday, addDaysToDateOnly, formatDateOnly } from "../../../../utils/berlinDate";
import { LAST_ACTIVE_DAY_PRESET } from "../../../filter/TabFilterModal/RollingDayPresets";

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// Object-table row cap for the click-triggered tooltip's object list —
// "Zeige X von Y Objekten" covers the rest (mirrors ResourceStatusMonitor's
// / AggregationKPI's / EfficiencyKPI's identically-named constant).
export const TOOLTIP_OBJECT_LIMIT = 100;

// Mirrors useResourceStatusMonitor.js's resolveRollingDate: an offset stores
// a RELATIVE anchor (day-N, or the "last active day" sentinel), never a fixed
// date, so it must be recomputed on every fetch — 0 ("Heute") is a valid,
// falsy offset, so this must check `=== null || === undefined`, never `!offset`.
const resolveRollingDate = (offset, fallbackDate, resourceLastActiveDate) => {
  if (offset === LAST_ACTIVE_DAY_PRESET) return resourceLastActiveDate || fallbackDate;
  if (offset === null || offset === undefined) return fallbackDate;
  return formatDateOnly(addDaysToDateOnly(getBerlinToday(), offset));
};

const normalizeSelectedDateForGranularity = (selectedDate, granularity) => {
  if (!selectedDate || granularity === "day") return selectedDate;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(selectedDate);
  if (!m) return selectedDate;

  const yearStr = m[1]; // keep as string to preserve zero-padding
  const month = Number(m[2]);
  const day = Number(m[3]);
  const year = Number(yearStr);
  const pad2 = (n) => String(n).padStart(2, "0");

  if (granularity === "month") {
    return `${yearStr}-${pad2(month)}-01`;
  }
  if (granularity === "year") {
    return `${yearStr}-01-01`;
  }
  if (granularity === "week") {
    const dt = new Date(Date.UTC(year, month - 1, day));
    const mondayOffset = (dt.getUTCDay() + 6) % 7; // Mon=0...Sun=6
    dt.setUTCDate(dt.getUTCDate() - mondayOffset);
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
  }
  return selectedDate;
};

const useResourceUtilizationChart = (activeFile, initialSettings = null) => {
  const api = useGlobalAPI(activeFile);
  const metadata = useOcelMetadata(activeFile);
  const { filterState } = useGlobalFilters();
  const configKey = `resourceUtilizationChartConfig_${activeFile || "default"}`;
  const viewKey = `resourceUtilizationChartView_${activeFile || "default"}`;

  // --- Data / loading state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null); // full response
  const [datasetLastDate, setDatasetLastDate] = useState(null); // last event date in dataset

  // --- Metadata state ---
  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [availableResources, setAvailableResources] = useState([]);
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // --- TileConfig (persistent, set in Edit Mode) ---
  const [title, setTitle] = useState("Resource Utilization Chart");
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [cycleStartActivity, setCycleStartActivity] = useState("cycle_start");
  const [cycleEndActivity, setCycleEndActivity] = useState("cycle_end");
  const [configuredResources, setConfiguredResources] = useState([]);
  const [tooltipAttributes, setTooltipAttributes] = useState([]);
  const [shiftStart, setShiftStart] = useState("06:00");
  const [shiftEnd, setShiftEnd] = useState("14:00");
  const [breaks, setBreaks] = useState([]);
  const [toleranceThreshold, setToleranceThreshold] = useState(60);
  const [ignoreShortTransitions, setIgnoreShortTransitions] = useState(false);
  const [includeNonWorkingDays, setIncludeNonWorkingDays] = useState(false);
  const [includeNoDataResources, setIncludeNoDataResources] = useState(false);
  const [multiResourceMode, setMultiResourceMode] = useState("accumulated");
  const [enabledGranularities, setEnabledGranularities] = useState([
    "day",
    "week",
    "month",
    "year",
  ]);
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonMode, setComparisonMode] = useState("previousPeriod");
  const [comparisonPeriodsCount, setComparisonPeriodsCount] = useState(1);
  const [limitComparisonToCurrentProgress, setLimitComparisonToCurrentProgress] = useState(false);
  const [showMovingAverage, setShowMovingAverage] = useState(false);
  const [resourceColumn, setResourceColumn] = useState("ressource");

  // --- ViewState (runtime, set in View Mode) ---
  const [viewResources, setViewResources] = useState([]);
  const [granularity, setGranularity] = useState("day");
  const [selectedDate, setSelectedDate] = useState(yesterdayISO());
  const [viewMode, setViewMode] = useState("granularity"); // "granularity" | "daterange"
  const [dateRangeStart, setDateRangeStart] = useState(yesterdayISO());
  const [dateRangeEnd, setDateRangeEnd] = useState(yesterdayISO());
  // Active rolling-preset offset for Granularity mode, or null when
  // selectedDate is a manually picked, frozen date (see resolveRollingDate
  // above). Never applies to Date Range mode.
  const [rollingOffset, setRollingOffset] = useState(null);
  // Last day THIS tile's own configuredResources had any event — feeds the
  // "Letzter aktiver Tag" preset. Resource-scoped, not dataset-wide.
  const [resourceLastActiveDate, setResourceLastActiveDate] = useState(null);
  // Mirrors rollingOffset for the dataset-lastDate clamp effects below,
  // without adding rollingOffset to their dependency arrays.
  const rollingOffsetRef = useRef(rollingOffset);
  useEffect(() => {
    rollingOffsetRef.current = rollingOffset;
  }, [rollingOffset]);

  // --- Request dedup ref ---
  const inFlightSignatureRef = useRef(null);
  const lastResolvedSignatureRef = useRef(null);
  const lastResolvedAtRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  // --- Restore TileConfig from localStorage ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(configKey);
      if (!raw) return;
      const cfg = JSON.parse(raw);

      if (typeof cfg.title === "string") setTitle(cfg.title);
      if (typeof cfg.selectedObjectType === "string")
        setSelectedObjectType(cfg.selectedObjectType);
      if (typeof cfg.cycleStartActivity === "string")
        setCycleStartActivity(cfg.cycleStartActivity);
      if (typeof cfg.cycleEndActivity === "string")
        setCycleEndActivity(cfg.cycleEndActivity);
      if (Array.isArray(cfg.configuredResources))
        setConfiguredResources(cfg.configuredResources);
      if (Array.isArray(cfg.tooltipAttributes))
        setTooltipAttributes(cfg.tooltipAttributes);
      if (typeof cfg.shiftStart === "string") setShiftStart(cfg.shiftStart);
      if (typeof cfg.shiftEnd === "string") setShiftEnd(cfg.shiftEnd);
      if (Array.isArray(cfg.breaks)) setBreaks(cfg.breaks);
      if (typeof cfg.toleranceThreshold === "number")
        setToleranceThreshold(cfg.toleranceThreshold);
      if (typeof cfg.ignoreShortTransitions === "boolean")
        setIgnoreShortTransitions(cfg.ignoreShortTransitions);
      if (typeof cfg.includeNonWorkingDays === "boolean")
        setIncludeNonWorkingDays(cfg.includeNonWorkingDays);
      if (typeof cfg.includeNoDataResources === "boolean")
        setIncludeNoDataResources(cfg.includeNoDataResources);
      if (typeof cfg.multiResourceMode === "string")
        setMultiResourceMode(cfg.multiResourceMode);
      if (
        Array.isArray(cfg.enabledGranularities) &&
        cfg.enabledGranularities.length > 0
      )
        setEnabledGranularities(cfg.enabledGranularities);
      if (typeof cfg.comparisonEnabled === "boolean")
        setComparisonEnabled(cfg.comparisonEnabled);
      if (cfg.comparisonMode === "previousPeriod" || cfg.comparisonMode === "yoy")
        setComparisonMode(cfg.comparisonMode);
      if (
        typeof cfg.comparisonPeriodsCount === "number" &&
        cfg.comparisonPeriodsCount >= 1 &&
        cfg.comparisonPeriodsCount <= 4
      )
        setComparisonPeriodsCount(cfg.comparisonPeriodsCount);
      if (typeof cfg.limitComparisonToCurrentProgress === "boolean")
        setLimitComparisonToCurrentProgress(cfg.limitComparisonToCurrentProgress);
      if (typeof cfg.showMovingAverage === "boolean")
        setShowMovingAverage(cfg.showMovingAverage);
    } catch {
      // ignore
    }
  }, [configKey]);

  // --- Restore ViewState from localStorage ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(viewKey);
      if (!raw) return;
      const vs = JSON.parse(raw);

      if (typeof vs.granularity === "string") setGranularity(vs.granularity);
      if (Array.isArray(vs.viewResources)) setViewResources(vs.viewResources);
      if (typeof vs.selectedDate === "string" && DATE_RE.test(vs.selectedDate)) setSelectedDate(vs.selectedDate);
      if (vs.viewMode === "granularity" || vs.viewMode === "daterange")
        setViewMode(vs.viewMode);
      if (typeof vs.dateRangeStart === "string" && DATE_RE.test(vs.dateRangeStart))
        setDateRangeStart(vs.dateRangeStart);
      if (typeof vs.dateRangeEnd === "string" && DATE_RE.test(vs.dateRangeEnd)) setDateRangeEnd(vs.dateRangeEnd);
      if (
        vs.rollingOffset === null ||
        typeof vs.rollingOffset === "number" ||
        vs.rollingOffset === LAST_ACTIVE_DAY_PRESET
      )
        setRollingOffset(vs.rollingOffset);
    } catch {
      // ignore
    }
  }, [viewKey]);

  // Restore from centralised config (overrides localStorage on mount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    const s = initialSettings;
    if (s.title !== undefined) setTitle(s.title);
    if (s.selectedObjectType !== undefined) setSelectedObjectType(s.selectedObjectType);
    if (s.cycleStartActivity !== undefined) setCycleStartActivity(s.cycleStartActivity);
    if (s.cycleEndActivity !== undefined) setCycleEndActivity(s.cycleEndActivity);
    if (s.configuredResources !== undefined) setConfiguredResources(s.configuredResources);
    if (s.tooltipAttributes !== undefined) setTooltipAttributes(s.tooltipAttributes);
    if (s.shiftStart !== undefined) setShiftStart(s.shiftStart);
    if (s.shiftEnd !== undefined) setShiftEnd(s.shiftEnd);
    if (s.breaks !== undefined) setBreaks(s.breaks);
    if (s.toleranceThreshold !== undefined) setToleranceThreshold(s.toleranceThreshold);
    if (s.ignoreShortTransitions !== undefined) setIgnoreShortTransitions(s.ignoreShortTransitions);
    if (s.includeNonWorkingDays !== undefined) setIncludeNonWorkingDays(s.includeNonWorkingDays);
    if (s.includeNoDataResources !== undefined) setIncludeNoDataResources(s.includeNoDataResources);
    if (s.multiResourceMode !== undefined) setMultiResourceMode(s.multiResourceMode);
    if (s.enabledGranularities !== undefined) setEnabledGranularities(s.enabledGranularities);
    if (s.comparisonEnabled !== undefined) setComparisonEnabled(s.comparisonEnabled);
    if (s.comparisonMode !== undefined) setComparisonMode(s.comparisonMode);
    if (s.comparisonPeriodsCount !== undefined) setComparisonPeriodsCount(s.comparisonPeriodsCount);
    if (s.limitComparisonToCurrentProgress !== undefined)
      setLimitComparisonToCurrentProgress(s.limitComparisonToCurrentProgress);
    if (s.showMovingAverage !== undefined) setShowMovingAverage(s.showMovingAverage);
    if (s.viewResources !== undefined) setViewResources(s.viewResources);
    if (s.granularity !== undefined) setGranularity(s.granularity);
    if (s.selectedDate !== undefined) setSelectedDate(s.selectedDate);
    if (s.viewMode !== undefined) setViewMode(s.viewMode);
    if (s.dateRangeStart !== undefined) setDateRangeStart(s.dateRangeStart);
    if (s.dateRangeEnd !== undefined) setDateRangeEnd(s.dateRangeEnd);
    if (s.rollingOffset !== undefined) setRollingOffset(s.rollingOffset);
  }, []);

  // --- Persist TileConfig ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        configKey,
        JSON.stringify({
          title,
          selectedObjectType,
          cycleStartActivity,
          cycleEndActivity,
          configuredResources,
          tooltipAttributes,
          shiftStart,
          shiftEnd,
          breaks,
          toleranceThreshold,
          ignoreShortTransitions,
          includeNonWorkingDays,
          includeNoDataResources,
          multiResourceMode,
          enabledGranularities,
          comparisonEnabled,
          comparisonMode,
          comparisonPeriodsCount,
          limitComparisonToCurrentProgress,
          showMovingAverage,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    configKey,
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    tooltipAttributes,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
    comparisonEnabled,
    comparisonMode,
    comparisonPeriodsCount,
    limitComparisonToCurrentProgress,
    showMovingAverage,
  ]);

  // --- Persist ViewState ---
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        viewKey,
        JSON.stringify({
          granularity,
          viewResources,
          selectedDate,
          viewMode,
          dateRangeStart,
          dateRangeEnd,
          rollingOffset,
        }),
      );
    } catch {
      /* no-op */
    }
  }, [
    viewKey,
    granularity,
    viewResources,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    rollingOffset,
  ]);

  // --- Break helpers ---
  const addBreak = useCallback(() => {
    setBreaks((prev) => [...prev, { start: "12:00", end: "12:30" }]);
  }, []);

  const removeBreak = useCallback((idx) => {
    setBreaks((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const updateBreak = useCallback((idx, field, value) => {
    setBreaks((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    );
  }, []);

  // --- Reactive metadata sync from OcelContext (zero API calls when no filters active) ---
  useEffect(() => {
    if (!metadata?.object_types) return;
    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );
    if (hasActiveFilters) return;

    const types = Object.keys(metadata.object_types);
    setObjectTypes(types);

    const activities = metadata.event_types || [];
    const activitiesMap = types.reduce((acc, t) => {
      acc[t] = activities;
      return acc;
    }, {});
    setObjectTypeActivities(activitiesMap);

    if (metadata.resource_values) {
      setAvailableResources(metadata.resource_values);
    }
    if (metadata.resource_column) {
      setResourceColumn(metadata.resource_column);
    }

    if (metadata.date_range?.end) {
      const lastDate = metadata.date_range.end.slice(0, 10);
      setDatasetLastDate(lastDate);
      // Skip the clamp while a rolling preset is active — it would otherwise
      // silently override the freshly-resolved rolling date (see
      // resolveRollingDate above / useResourceStatusMonitor.js's identical
      // guard on this same kind of dataset-lastDate clamp).
      if (rollingOffsetRef.current === null || rollingOffsetRef.current === undefined) {
        setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
      }
    }

    setIsMetadataLoaded(true);
  }, [metadata, filterState, activeFile]);

  // --- Resource-scoped "last active day" — refetched whenever the tile's own
  // committed resources (or the resolved resource column) change, so the
  // LAST_ACTIVE_DAY_PRESET pill always reflects the current configuration ---
  useEffect(() => {
    if (!activeFile || configuredResources.length === 0) {
      setResourceLastActiveDate(null);
      return;
    }
    let cancelled = false;
    getResourceLastActiveDate(activeFile, configuredResources, resourceColumn)
      .then((res) => {
        if (!cancelled) setResourceLastActiveDate(res?.last_active_date ?? null);
      })
      .catch(() => {
        if (!cancelled) setResourceLastActiveDate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeFile, configuredResources, resourceColumn]);

  // --- Keep displayed selectedDate in sync with the resolved rolling date
  // (Granularity mode only — Date Range mode never reads selectedDate) ---
  useEffect(() => {
    if (viewMode !== "granularity") return;
    if (rollingOffset === null || rollingOffset === undefined) return;
    setSelectedDate((prev) => {
      const resolved = resolveRollingDate(rollingOffset, prev, resourceLastActiveDate);
      return resolved === prev ? prev : resolved;
    });
  }, [viewMode, rollingOffset, resourceLastActiveDate]);

  // --- Auto-select first object type ---
  useEffect(() => {
    if (!selectedObjectType && objectTypes.length > 0) {
      setSelectedObjectType(objectTypes[0]);
    }
  }, [objectTypes, selectedObjectType]);

  // --- Fetch metadata ---
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    const hasActiveFilters = Object.values(filterState).some((v) =>
      Array.isArray(v) ? v.length > 0 : v?.enabled === true,
    );

    // When no filters are active, the reactive effect already synced metadata — nothing to do
    if (!hasActiveFilters) {
      setIsMetadataLoaded(true);
      return true;
    }

    setLoading(true);
    setError(null);

    const basicRequest = { file_type: activeFile };

    const tryPostEndpoints = async (endpoints, payload, label) => {
      const tried = [];
      for (const ep of endpoints) {
        try {
          const res = await api.post(ep, payload, {}, label);
          return { res, endpoint: ep };
        } catch (e) {
          tried.push({ endpoint: ep, message: e?.message || String(e) });
          if (e?.message && /not found|404/i.test(e.message)) continue;
          throw e;
        }
      }
      const details = tried
        .map((t) => `${t.endpoint} -> ${t.message}`)
        .join(" | ");
      throw new Error(`All ${label} endpoints failed: ${details}`);
    };

    const tryGetEndpoints = async (endpoints, params, label) => {
      const tried = [];
      for (const ep of endpoints) {
        try {
          const res = await api.get(ep, params, {}, label);
          return { res, endpoint: ep };
        } catch (e) {
          tried.push({ endpoint: ep, message: e?.message || String(e) });
          if (e?.message && /not found|404/i.test(e.message)) continue;
          throw e;
        }
      }
      const details = tried
        .map((t) => `${t.endpoint} -> ${t.message}`)
        .join(" | ");
      throw new Error(`All ${label} endpoints failed: ${details}`);
    };

    try {
      const { res: objectTypesResponse } = await tryPostEndpoints(
        [
          "/filtered-object-types",
          "/ocel/filtered-object-types",
          "/v1/filtered-object-types",
        ],
        basicRequest,
        "Object Types",
      );
      const types = objectTypesResponse.objectTypes || [];
      setObjectTypes(types);
      if (!selectedObjectType && types.length > 0) {
        setSelectedObjectType(types[0]);
      }

      let activitiesResponse;
      try {
        const { res } = await tryPostEndpoints(
          [
            "/object-type-activities",
            "/ocel/object-type-activities",
            "/v1/object-type-activities",
          ],
          basicRequest,
          "Object Type Activities",
        );
        activitiesResponse = res;
      } catch (postErr) {
        try {
          activitiesResponse = await api.getRaw(
            "/object-type-activities",
            { file_type: activeFile },
            {},
            "Object Type Activities (GET)",
          );
        } catch {
          throw postErr;
        }
      }
      const activitiesMap = activitiesResponse.objectTypeActivities || {};
      setObjectTypeActivities(activitiesMap);

      const { res: resourceResponse } = await tryGetEndpoints(
        ["/ocel/resource-values", "/resource-values", "/v1/resource-values"],
        { file_type: activeFile },
        "Resource Values",
      );

      const resources = resourceResponse.values || [];
      setAvailableResources(resources);
      setConfiguredResources((prev) => {
        const nextConfigured = prev.filter((r) => resources.includes(r));
        setViewResources((existingView) => {
          const filteredView = existingView.filter((r) =>
            resources.includes(r),
          );
          if (filteredView.length > 0) return filteredView;
          return nextConfigured;
        });
        return nextConfigured;
      });

      if (resourceResponse.used_column) {
        setResourceColumn(resourceResponse.used_column.replace(/^ocel:/, ""));
      }

      // Fetch dataset date range and snap selectedDate if it falls outside
      try {
        const metaRes = await getOCELMetadata(activeFile);
        if (metaRes?.date_range?.end) {
          const lastDate = metaRes.date_range.end.slice(0, 10);
          setDatasetLastDate(lastDate);
          if (rollingOffsetRef.current === null || rollingOffsetRef.current === undefined) {
            setSelectedDate((prev) => (prev > lastDate ? lastDate : prev));
          }
        }
      } catch {
        // Non-critical — ignore failures
      }

      setIsMetadataLoaded(true);
      return true;
    } catch (err) {
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api, filterState, selectedObjectType]);

  // --- Fetch chart data ---
  const fetchChartData = useCallback(
    async (overrides = {}) => {
      const effectiveConfiguredResources =
        overrides.configuredResources ?? configuredResources;
      const effectiveViewResources = overrides.viewResources ?? viewResources;
      const effectiveResources =
        effectiveViewResources.length > 0
          ? effectiveViewResources
          : effectiveConfiguredResources;
      const effectiveSelectedObjectType =
        overrides.selectedObjectType ?? selectedObjectType;
      const effectiveShiftStart = overrides.shiftStart ?? shiftStart;
      const effectiveShiftEnd = overrides.shiftEnd ?? shiftEnd;
      const effectiveBreaks = overrides.breaks ?? breaks;
      const effectiveToleranceThreshold =
        overrides.toleranceThreshold ?? toleranceThreshold;
      const effectiveIgnoreShortTransitions =
        overrides.ignoreShortTransitions ?? ignoreShortTransitions;
      const effectiveIncludeNonWorkingDays =
        overrides.includeNonWorkingDays ?? includeNonWorkingDays;
      const effectiveIncludeNoDataResources =
        overrides.includeNoDataResources ?? includeNoDataResources;
      const effectiveMultiResourceMode =
        overrides.multiResourceMode ?? multiResourceMode;
      const effectiveGranularity = overrides.granularity ?? granularity;
      const effectiveEnabledGranularities =
        overrides.enabledGranularities ?? enabledGranularities;
      const effectiveComparisonEnabled =
        overrides.comparisonEnabled ?? comparisonEnabled;
      const effectiveComparisonMode = overrides.comparisonMode ?? comparisonMode;
      const effectiveComparisonPeriodsCount =
        overrides.comparisonPeriodsCount ?? comparisonPeriodsCount;
      const effectiveLimitComparisonToCurrentProgress =
        overrides.limitComparisonToCurrentProgress ?? limitComparisonToCurrentProgress;
      const effectiveShowMovingAverage =
        overrides.showMovingAverage ?? showMovingAverage;
      const effectiveViewMode = overrides.viewMode ?? viewMode;
      const effectiveDateRangeStart = overrides.dateRangeStart ?? dateRangeStart;
      const effectiveDateRangeEnd = overrides.dateRangeEnd ?? dateRangeEnd;
      const effectiveRollingOffset =
        overrides.rollingOffset !== undefined ? overrides.rollingOffset : rollingOffset;
      // Rolling presets only apply in Granularity mode — Date Range mode's
      // selectedDate is just a redundant echo of dateRangeStart and must stay
      // untouched (see the isDateRange branch of the request payload below).
      const effectiveSelectedDate =
        effectiveViewMode === "daterange"
          ? (overrides.selectedDate ?? selectedDate)
          : resolveRollingDate(
              effectiveRollingOffset,
              overrides.selectedDate ?? selectedDate,
              resourceLastActiveDate,
            );
      const normalizedGranularityDate = normalizeSelectedDateForGranularity(
        effectiveSelectedDate,
        effectiveGranularity,
      );

      if (!activeFile) {
        setError("No active file selected");
        return false;
      }
      if (!effectiveSelectedObjectType) {
        setError("Please select an object type");
        return false;
      }
      if (effectiveResources.length === 0) {
        setError("Please select at least one resource");
        return false;
      }
      const isDateRange = effectiveViewMode === "daterange";
      if (!isDateRange && !effectiveSelectedDate) {
        setError("Please select a date");
        return false;
      }
      if (isDateRange && (!effectiveDateRangeStart || !effectiveDateRangeEnd)) {
        setError("Please select start and end dates");
        return false;
      }
      if (!isDateRange && normalizedGranularityDate && !DATE_RE.test(normalizedGranularityDate)) {
        setError("Invalid date format. Please re-select a date.");
        setSelectedDate(yesterdayISO());
        return false;
      }

      const toTimestamp = (hhmm) => `${hhmm}:00`;

      // Comparison periods only apply in Granularity mode (mirrors the Attribute Sum
      // Line Chart's comparison feature, which is also restricted to non-daterange
      // views) — compute up to 4 previous-period ranges relative to the current
      // period, dropping any that resolve to an empty range.
      let comparisonPeriodsPayload = null;
      if (!isDateRange && effectiveComparisonEnabled) {
        const currentRange = computeDisplayRange(normalizedGranularityDate, effectiveGranularity);
        let currentRangeForComparison = {
          start: currentRange.startDate,
          end: currentRange.endDate,
        };
        if (effectiveLimitComparisonToCurrentProgress) {
          // Clamp the CURRENT period's end to today before deriving comparison
          // ranges — getComparisonRange anchors each comparison period's end to
          // this range's end, so a still-in-progress year/month/week (e.g. only
          // through August) compares against the SAME elapsed portion of prior
          // periods instead of their full, already-complete length.
          currentRangeForComparison = clampRangeEndToToday(
            currentRangeForComparison,
            effectiveGranularity,
            formatDateOnly(getBerlinToday()),
          );
        }
        const periods = [];
        for (let periodsBack = 1; periodsBack <= effectiveComparisonPeriodsCount; periodsBack++) {
          const compRange = getComparisonRange(
            effectiveComparisonMode,
            effectiveGranularity,
            currentRangeForComparison,
            periodsBack,
          );
          if (!compRange.start || !compRange.end) continue;
          const periodLabel = formatComparisonPeriodLabel(compRange.start, compRange.end, effectiveGranularity);
          periods.push({
            period_start: compRange.start,
            period_end: compRange.end,
            period_label: periodLabel,
          });
        }
        if (periods.length > 0) comparisonPeriodsPayload = periods;
      }

      const payload = {
        file_type: activeFile,
        resources: effectiveResources,
        shift: {
          start_time: toTimestamp(effectiveShiftStart),
          end_time: toTimestamp(effectiveShiftEnd),
        },
        breaks: effectiveBreaks.map((b) => ({
          start_time: toTimestamp(b.start),
          end_time: toTimestamp(b.end),
        })),
        tolerance_threshold_seconds: effectiveToleranceThreshold,
        ignore_short_transitions: effectiveIgnoreShortTransitions,
        include_non_working_days: effectiveIncludeNonWorkingDays,
        include_no_data_resources: effectiveIncludeNoDataResources,
        multi_resource_mode: effectiveMultiResourceMode,
        cycle_start_column: cycleStartActivity,
        cycle_end_column: cycleEndActivity,
        production_order_column: effectiveSelectedObjectType,
        resource_column: resourceColumn,
        include_moving_average_lookback: effectiveShowMovingAverage,
        ...(isDateRange
          ? {
              period_start: effectiveDateRangeStart,
              period_end: effectiveDateRangeEnd,
              granularity: "day",
              selected_date: effectiveDateRangeStart,
            }
          : {
              granularity: effectiveGranularity,
              selected_date: normalizedGranularityDate,
              enabled_granularities: effectiveEnabledGranularities,
            }),
        ...(comparisonPeriodsPayload
          ? {
              comparison_enabled: true,
              comparison_mode: effectiveComparisonMode,
              comparison_periods: comparisonPeriodsPayload,
            }
          : {}),
      };

      const requestSignature = JSON.stringify({
        ...payload,
        resources: [...payload.resources].sort(),
        ...(payload.enabled_granularities
          ? { enabled_granularities: [...payload.enabled_granularities].sort() }
          : {}),
      });

      if (inFlightSignatureRef.current === requestSignature) return true;
      const now = Date.now();
      if (
        lastResolvedSignatureRef.current === requestSignature &&
        now - lastResolvedAtRef.current < 1000
      ) {
        return true;
      }

      setLoading(true);
      setError(null);
      const requestId = ++latestRequestIdRef.current;

      try {
        inFlightSignatureRef.current = requestSignature;
        const response = await api.post(
          "/kpi/resource-utilization-chart",
          payload,
          {},
          "Resource Utilization Chart",
        );
        if (requestId !== latestRequestIdRef.current) return true;
        setChartData(response);
        lastResolvedSignatureRef.current = requestSignature;
        lastResolvedAtRef.current = Date.now();
        return true;
      } catch (err) {
        if (requestId !== latestRequestIdRef.current) return false;
        setChartData(null);
        let msg = err?.message || "Failed to load chart data";
        if (msg.includes("404")) {
          msg = "Endpoint not found. Please check if the backend is running.";
        } else if (msg.includes("400")) {
          msg = "Invalid configuration. Please check your settings.";
        }
        setError(msg);
        return false;
      } finally {
        if (inFlightSignatureRef.current === requestSignature) {
          inFlightSignatureRef.current = null;
        }
        if (requestId === latestRequestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [
      activeFile,
      api,
      breaks,
      configuredResources,
      cycleEndActivity,
      cycleStartActivity,
      enabledGranularities,
      granularity,
      includeNonWorkingDays,
      includeNoDataResources,
      ignoreShortTransitions,
      multiResourceMode,
      resourceColumn,
      selectedDate,
      rollingOffset,
      resourceLastActiveDate,
      selectedObjectType,
      shiftEnd,
      shiftStart,
      toleranceThreshold,
      viewResources,
      viewMode,
      dateRangeStart,
      dateRangeEnd,
      comparisonEnabled,
      comparisonMode,
      comparisonPeriodsCount,
      limitComparisonToCurrentProgress,
      showMovingAverage,
    ],
  );

  // --- Fetch object breakdown for one bar (click-triggered tooltip) ---
  // Unlike fetchChartData, this always fires against the currently-COMMITTED
  // chart (no effective*/overrides indirection — that indirection exists
  // only so the Settings modal can preview draft values before Apply, which
  // doesn't apply to a click on the already-rendered chart).
  const fetchObjectBreakdown = useCallback(
    async (bucketStart, bucketEnd, selectedAttributes) => {
      const toTimestamp = (hhmm) => `${hhmm}:00`;
      const payload = {
        file_type: activeFile,
        resources: viewResources.length > 0 ? viewResources : configuredResources,
        shift: {
          start_time: toTimestamp(shiftStart),
          end_time: toTimestamp(shiftEnd),
        },
        breaks: breaks.map((b) => ({
          start_time: toTimestamp(b.start),
          end_time: toTimestamp(b.end),
        })),
        tolerance_threshold_seconds: toleranceThreshold,
        ignore_short_transitions: ignoreShortTransitions,
        include_non_working_days: includeNonWorkingDays,
        include_no_data_resources: includeNoDataResources,
        multi_resource_mode: multiResourceMode,
        cycle_start_column: cycleStartActivity,
        cycle_end_column: cycleEndActivity,
        production_order_column: selectedObjectType,
        resource_column: resourceColumn,
        bucket_start: bucketStart,
        bucket_end: bucketEnd,
        selected_attributes: selectedAttributes,
        limit: TOOLTIP_OBJECT_LIMIT,
      };
      return api.post(
        "/kpi/resource-utilization-object-breakdown",
        payload,
        {},
        "Resource Utilization Object Breakdown",
      );
    },
    [
      activeFile,
      viewResources,
      configuredResources,
      shiftStart,
      shiftEnd,
      breaks,
      toleranceThreshold,
      ignoreShortTransitions,
      includeNonWorkingDays,
      includeNoDataResources,
      multiResourceMode,
      cycleStartActivity,
      cycleEndActivity,
      selectedObjectType,
      resourceColumn,
      api,
    ],
  );

  // --- Reset on file change (only chart output — metadata is repopulated by the sync effect) ---
  useEffect(() => {
    setChartData(null);
    setError(null);
  }, [activeFile]);

  // --- Keep viewResources in sync with configuredResources ---
  useEffect(() => {
    setViewResources((prev) => {
      const allowed = prev.filter((r) => configuredResources.includes(r));
      if (allowed.length > 0) return allowed;
      return configuredResources;
    });
  }, [configuredResources]);

  return {
    // Data
    loading,
    error,
    chartData,
    datasetLastDate,
    // Metadata
    objectTypes,
    objectTypeActivities,
    availableResources,
    isMetadataLoaded,
    // TileConfig
    title,
    selectedObjectType,
    cycleStartActivity,
    cycleEndActivity,
    configuredResources,
    tooltipAttributes,
    shiftStart,
    shiftEnd,
    breaks,
    toleranceThreshold,
    ignoreShortTransitions,
    includeNonWorkingDays,
    includeNoDataResources,
    multiResourceMode,
    enabledGranularities,
    comparisonEnabled,
    comparisonMode,
    comparisonPeriodsCount,
    limitComparisonToCurrentProgress,
    showMovingAverage,
    // ViewState
    viewResources,
    granularity,
    selectedDate,
    viewMode,
    dateRangeStart,
    dateRangeEnd,
    rollingOffset,
    resourceLastActiveDate,
    // TileConfig setters
    setTitle,
    setSelectedObjectType,
    setCycleStartActivity,
    setCycleEndActivity,
    setConfiguredResources,
    setTooltipAttributes,
    setShiftStart,
    setShiftEnd,
    setBreaks,
    setToleranceThreshold,
    setIgnoreShortTransitions,
    setIncludeNonWorkingDays,
    setIncludeNoDataResources,
    setMultiResourceMode,
    setEnabledGranularities,
    setComparisonEnabled,
    setComparisonMode,
    setComparisonPeriodsCount,
    setLimitComparisonToCurrentProgress,
    setShowMovingAverage,
    // ViewState setters
    setViewResources,
    setGranularity,
    setSelectedDate,
    setViewMode,
    setDateRangeStart,
    setDateRangeEnd,
    setRollingOffset,
    // Break helpers
    addBreak,
    removeBreak,
    updateBreak,
    // Actions
    fetchMetadata,
    fetchChartData,
    fetchObjectBreakdown,
  };
};

export { useResourceUtilizationChart };
